import React, { useState } from 'react';
import ETLogo from '../../../../assets/environment-testing.png';
import { useNavigate } from 'react-router';
import {
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    Button,
    Paper,
    List,
    ListItem,
    ListItemText,
    Container,
    Link
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';

export const InstructionPage = () => {
    const navigate = useNavigate();
    const [isAgreed, setIsAgreed] = useState(false);

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsAgreed(event.target.checked);
    };

    const handleNextStep = () => {
        if (isAgreed) {
            navigate('/account-registration');
        }
    };

    const instructions = [
        "This form is used to submit a new client account request.",
        "Please ensure all required information is accurate and complete.",
        "By proceeding, you confirm that the information provided is correct to the best of your knowledge.",
        "To avoid losing any entered data, please keep this page open until your submission is complete."
    ];

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(to right, #003883, #ee7d11)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4
            }}
        >
            <Container maxWidth="md">
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 3, md: 5 },
                        borderRadius: 3,
                        background: '#ffffff',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(0, 0, 0, 0.08)',
                        border: '1px solid #e8e8e8',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '5px',
                            background: 'linear-gradient(90deg, #003883 0%, #0056b3 100%)',
                        }
                    }}
                >
                    <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ mb: 2, textAlign: 'center' }}>
                            <Link
                                href="https://www.eurofins.com.au/environment-testing/"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ display: 'inline-block' }}
                            >
                                <Box
                                    component="img"
                                    src={ETLogo}
                                    alt="Eurofins Environment Testing Logo"
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                    }}
                                    sx={{
                                        width: {
                                            xs: '260px', // mobile
                                            sm: '320px', // tablet
                                            md: '450px', // desktop
                                        },
                                        maxWidth: '100%',
                                        height: 'auto',
                                        mb: 3,
                                    }}
                                />
                            </Link>
                        </Box>
                        <Typography
                            variant="h5"
                            component="h1"
                            sx={{
                                fontWeight: 600,
                                mb: 2,
                                mt: 2,
                                fontSize: '18px',
                                color: 'secondary.main'
                            }}
                        >
                            New Client Application Form
                        </Typography>
                    </Box>


                    <Box sx={{ textAlign: 'left', mb: 2 }}>
                        <List sx={{ py: 0 }}>
                            {instructions.map((instruction, index) => (
                                <ListItem
                                    key={index}
                                    sx={{
                                        py: 1.5,
                                        px: 0,
                                        alignItems: 'flex-start'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            bgcolor: 'primary.main',
                                            mt: 1,
                                            mr: 2,
                                            flexShrink: 0
                                        }}
                                    />
                                    <ListItemText
                                        primary={
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    lineHeight: 1.6,
                                                    color: 'text.primary'
                                                }}
                                            >
                                                {instruction}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>

                    <Box
                        sx={{
                            mb: 3,
                            px: { xs: 1.5, md: 2 },
                            py: { xs: 1.5, md: 1.75 },
                            borderRadius: 2,
                            backgroundColor: '#f7f9fc',
                            border: '1px solid rgba(0, 56, 131, 0.1)'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                            <Box
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '10px',
                                    bgcolor: 'rgba(0, 56, 131, 0.1)',
                                    color: '#003883',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <NoteAltOutlinedIcon sx={{ fontSize: 18 }} />
                            </Box>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, color: '#003883', lineHeight: 1.2 }}
                            >
                                Note
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'grid', gap: 1 }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <AccessTimeRoundedIcon sx={{ color: '#ee7d11', mt: 0.15, fontSize: 18 }} />
                                <Typography variant="body2" sx={{ lineHeight: 1.6, color: 'text.primary', fontSize: '13px' }}>
                                    Please complete the form within <b>one hour</b> of starting. If you are unable to finish within this time, you may <b>save your progress</b> and return later.
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <SaveOutlinedIcon sx={{ color: '#003883', mt: 0.15, fontSize: 18 }} />
                                <Typography variant="body2" sx={{ lineHeight: 1.6, color: 'text.primary', fontSize: '13px' }}>
                                    To resume, simply log in again using the <b>same email address</b> and you will be able to continue from where you left off.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 4, textAlign: 'left' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isAgreed}
                                    onChange={handleCheckboxChange}
                                    color="primary"
                                    data-testid="agreement-checkbox"
                                />
                            }
                            label={
                                <Typography variant="body1" sx={{ ml: 1, mt: 1 }}>
                                    I have read and understood the above information.
                                </Typography>
                            }
                            sx={{
                                alignItems: 'flex-start',
                                '& .MuiFormControlLabel-label': {
                                    mt: 0.5
                                }
                            }}
                        />
                    </Box>

                    <Box sx={{ textAlign: 'center' }}>
                        <Button
                            variant="contained"
                            size="medium"
                            onClick={handleNextStep}
                            disabled={!isAgreed}
                            endIcon={<ArrowForwardIcon />}
                            data-testid="next-step-button"
                            sx={{
                                fontWeight: 600,
                                textTransform: 'none',
                                fontSize: '12px',
                                minWidth: 120,
                                '&:disabled': {
                                    bgcolor: 'grey.300',
                                    color: 'grey.500'
                                }
                            }}
                        >
                            Next Step
                        </Button>
                    </Box>

                    {!isAgreed && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 2, display: 'block' }}
                        >
                            Please agree to the terms above to continue
                        </Typography>
                    )}
                </Paper>
            </Container>
        </Box>
    );
};